import React, { useEffect, useState } from 'react';

import Button from '../../components/ui/Button';
import Footer from '../homepage/components/Footer';
import Header from '../../components/ui/Header';
import { Helmet } from 'react-helmet';
import HeroSection from './components/HeroSection';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';
import PracticeAreaCard from './components/PracticeAreaCard';
import PracticeAreaModal from './components/PracticeAreaModal';
import { practiceAreasService } from '../../services/practiceAreasService';

const PracticeAreas = () => {
	const [selectedPracticeArea, setSelectedPracticeArea] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [practiceAreas, setPracticeAreas] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		loadPracticeAreas();
	}, []);

	const loadPracticeAreas = async () => {
		try {
			setLoading(true);
			const { data, error } = await practiceAreasService.getAllPracticeAreas();
			if (error) throw error;
			setPracticeAreas(data || []);
		} catch (error) {
			console.error('Error loading practice areas:', error);
			setError('Failed to load practice areas');
		} finally {
			setLoading(false);
		}
	};

	const handleLearnMore = (practiceArea) => {
		setSelectedPracticeArea(practiceArea);
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedPracticeArea(null);
	};

	return (
		<>
			<Helmet>
				<title>Practice Areas - Fepa Law Partners | Legal Services</title>
				<meta
					name='description'
					content='Explore our comprehensive range of legal practice areas including corporate law, litigation, real estate, and more. Expert legal services tailored to your needs.'
				/>
				<meta
					name='keywords'
					content='practice areas, legal services, corporate law, litigation, real estate law, criminal defense, family law, Fepa Law Partners'
				/>
			</Helmet>
			<div className='min-h-screen bg-gray-50'>
				<Header />
				<HeroSection />

				{/* Introduction Section */}
				<section className='py-16 bg-white'>
					<div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
						<h2 className='font-playfair font-bold text-3xl lg:text-4xl text-[#800020] mb-6'>
							Comprehensive Legal Services
						</h2>
						<p className='text-lg text-gray-600 leading-relaxed'>
							At FEPA Law Partners, we offer a wide range of legal services to meet the diverse
							needs of our clients. Our experienced attorneys specialize in various practice areas,
							ensuring that you receive expert legal representation tailored to your specific
							situation.
						</p>
					</div>
				</section>

				{/* Practice Areas Grid */}
				<section className='py-16'>
					<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
						{loading ? (
							<div className='flex items-center justify-center py-16'>
								<Icon name='Loader' className='h-8 w-8 animate-spin text-[#800020]' />
								<span className='ml-2 text-gray-600'>Loading practice areas...</span>
							</div>
						) : error ? (
							<div className='text-center py-16'>
								<Icon name='AlertCircle' className='h-12 w-12 mx-auto mb-4 text-red-500' />
								<p className='text-red-600 mb-4'>{error}</p>
								<Button
									onClick={loadPracticeAreas}
									className='bg-[#800020] hover:bg-[#800020]/90 text-white px-6 py-2 rounded-lg'
								>
									Try Again
								</Button>
							</div>
						) : practiceAreas.length === 0 ? (
							<div className='text-center py-16'>
								<Icon name='Briefcase' className='h-12 w-12 mx-auto mb-4 text-gray-300' />
								<p className='text-gray-500'>No practice areas found</p>
							</div>
						) : (
							<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
								{practiceAreas.map((practiceArea) => (
									<PracticeAreaCard
										key={practiceArea.id}
										practiceArea={practiceArea}
										onLearnMore={handleLearnMore}
									/>
								))}
							</div>
						)}
					</div>
				</section>

				{/* Related Services */}

				{/* CTA Section */}
				<section className='bg-gradient-to-r from-[#800020]/5 to-[#DB954E]/5 py-16'>
					<div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
						<h2 className='font-playfair font-bold text-3xl lg:text-4xl text-[#800020] mb-6'>
							Need Legal Assistance?
						</h2>
						<p className='text-lg text-gray-600 mb-8 max-w-2xl mx-auto'>
							Our experienced attorneys are ready to help you navigate complex legal matters.
							Contact us today for a consultation.
						</p>
						<div className='flex flex-col sm:flex-row gap-4 justify-center'>
							<Link to='/contact'>
								<Button
									variant='default'
									size='lg'
									className='bg-[#800020] hover:bg-[#800020]/90 text-white px-8 py-3 rounded-lg font-source font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-2'
								>
									<Icon name='Calendar' size={20} />
									<span>Schedule a Consultation</span>
								</Button>
							</Link>
							<Button
								variant='outline'
								size='lg'
								className='border-2 border-[#800020] text-[#800020] hover:bg-[#800020] hover:text-white px-8 py-3 rounded-lg font-source font-semibold transition-all duration-300 flex items-center justify-center space-x-2'
							>
								<Icon name='Phone' size={20} />
								<span>Call (+234) 909-552-9557</span>
							</Button>
						</div>
					</div>
				</section>

				<Footer />
			</div>

			{/* Practice Area Modal */}
			<PracticeAreaModal
				practiceArea={selectedPracticeArea}
				isOpen={isModalOpen}
				onClose={handleCloseModal}
			/>
		</>
	);
};

export default PracticeAreas;
